import React from 'react';
import { useTranslation } from 'react-i18next';
import isNumber from 'lodash/isNumber';
import { X_PATH } from 'constants/xPath';
import { Order, ColumnDefinition, FilterParams } from '#web-components/types/table';

import MdtuTable from '#web-components/components/Table';

interface Props<T> {
  columnDefinitions: ColumnDefinition[];
  list: T[];
  order: Order;
  orderField: string;
  onOrderChange?: (orderField: string, order: Order) => void;
  onFilterChange?: (filterParams: FilterParams) => void;
  emptyPlaceholder?: string;
  emptyPlaceholderIcon?: string | JSX.Element;
  hideEmptyPlaceholder?: boolean;
  pagination?: {
    page: number;
    rowsPerPage: number;
    totalItems?: number;
  }
}

export default function Table<T>(props: Props<T>) {
  const { t, i18n } = useTranslation('components');
  const {
    columnDefinitions,
    list,
    order,
    orderField,
    onOrderChange,
    emptyPlaceholder,
    emptyPlaceholderIcon,
    hideEmptyPlaceholder,
    pagination,
    onFilterChange,
  } = props;
  return (
    <MdtuTable
      columnDefinitions={columnDefinitions}
      list={list}
      order={order}
      orderField={orderField}
      onOrderChange={onOrderChange}
      emptyPlaceholder={emptyPlaceholder || t('table.emptyPlaceholder')}
      emptyPlaceholderIcon={emptyPlaceholderIcon}
      hideEmptyPlaceholder={hideEmptyPlaceholder}
      header={{
        'data-xpath': X_PATH.tableHeadCell,
      }}
      onFilterChange={onFilterChange}
      pagination={{
        ...pagination,
        labelRowsPerPage: t('table.perPage'),
        getDisplayedRowsLabel: (params) => (isNumber(params.count)
          ? t('table.pagination', params)
          : t('table.paginationNoCount', params)),
        'data-xpath': {
          tablePageCountSelect: X_PATH.tablePageCountSelect,
          tableFirstPage: X_PATH.tableFirstPage,
          tablePrevPage: X_PATH.tablePrevPage,
          tableNextPage: X_PATH.tableNextPage,
          tableLastPage: X_PATH.tableLastPage,
        },
        ariaLabel: {
          firstPage: t('table.aria.firstPage'),
          prevPage: t('table.aria.prevPage'),
          nextPage: t('table.aria.nextPage'),
          lastPage: t('table.aria.lastPage'),
        },
      }}
      data-xpath={{
        tableContainer: X_PATH.tableContainer,
        tableRow: X_PATH.tableRow,
      }}
      locale={i18n.language}
    />
  );
}
